import apiService from "@/lib/api";

/**
 * Record lookup API client — the GDPR / EU AI Act dossier.
 *
 * Every endpoint is camelCase, in the envelope and inside `core` and every
 * `rows[]` object. CV formatting next door is snake_case on purpose; do not
 * "correct" either one.
 *
 * Every route needs a JWT and `record.view`. A SuperAdmin scopes any route to
 * one tenant with `?tenantId=<uuid>` — the query parameter wins over the
 * ambient `X-Target-Tenant-Id` header.
 *
 * The all-tenants search passes `crossTenant: true`, or `@/lib/api` attaches
 * the ambient `X-Target-Tenant-Id` and the backend silently narrows the
 * search to whichever tenant the picker last selected.
 */

const RECORD_BASE = "/api/v2/record";

/** A super-admin search across tenants must not inherit the ambient scope. */
const tenantCrossTenant = (allTenants) => ({ crossTenant: !!allTenants });

/**
 * Pull the filename out of a Content-Disposition header.
 *
 * `/record`'s `_download()` emits RFC 5987 only — `filename*=UTF-8''<pct>` and
 * no plain `filename=` beside it — so the RFC 5987 form is read first and
 * percent-decoded. The plain form is the fallback, because other download
 * endpoints in this app still send it.
 */
export const parseContentDispositionFilename = (disposition = "") => {
  const extended = /filename\*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(disposition);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent sequence is still better read raw than dropped.
      return extended[1].trim();
    }
  }

  const plain = /filename=\s*"([^"]+)"|filename=\s*([^;]+)/.exec(disposition);
  if (plain) return (plain[1] ?? plain[2] ?? "").trim();

  return "";
};

/**
 * Trigger a browser download for a blob that came back over axios — a plain
 * `<a href>` carries no bearer token, so the blob route is the only one.
 */
/**
 * The JSON error envelope behind a failed blob request.
 *
 * `exportBundle` sets `responseType: "blob"`, so axios hands back a Blob on a
 * non-2xx too. Reading `error_code` straight off it always yields undefined,
 * which turns every export failure into the same unhelpful sentence.
 */
export const readBlobError = async (err) => {
  const raw = err?.response?.data;
  if (raw instanceof Blob && raw.size > 0) {
    try {
      return JSON.parse(await raw.text());
    } catch {
      /* not JSON, fall through */
    }
  }
  return typeof raw === "object" && raw !== null ? raw : {};
};

export const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const RecordService = {
  /**
   * Classify + search. `q` needs at least 2 characters (else 422
   * INVALID_RECORD_SEARCH). A UUID short-circuits: `results` is empty and
   * `resolved` says what the id is.
   *
   * @returns {Promise<{query, kind, resolved, results, degraded, truncated}>}
   */
  search: async ({ q, type = "candidate", allTenants = false, tenantId } = {}) => {
    const params = {
      q,
      type,
      allTenants: allTenants ? "true" : "false",
    };
    if (!allTenants && tenantId) params.tenantId = tenantId;
    const response = await apiService.getSecure(`${RECORD_BASE}/search`, { params, ...tenantCrossTenant(allTenants) });
    return response.data;
  },

  /**
   * The dossier index for one subject: `core` + per-section counts. Build the
   * left nav from `sections`, in the order returned.
   */
  getDossier: async ({ subjectType, subjectId, applicationId, tenantId } = {}) => {
    const params = {};
    if (subjectType === "candidate" && applicationId) params.application_id = applicationId;
    if (tenantId) params.tenantId = tenantId;
    const response = await apiService.getSecure(`${RECORD_BASE}/${subjectType}/${encodeURIComponent(subjectId)}`, { params });
    return response.data;
  },

  /**
   * One section, paged. Every section returns a different row shape; the
   * frontend builds columns from `Object.keys(rows[0])` and special-cases
   * only what needs it. `per_page` is capped at 200 server-side.
   */
  getSection: async ({ subjectType, subjectId, section, page = 1, perPage = 25, applicationId, tenantId } = {}) => {
    const params = { page, per_page: perPage };
    if (subjectType === "candidate" && applicationId) params.application_id = applicationId;
    if (tenantId) params.tenantId = tenantId;
    const response = await apiService.getSecure(`${RECORD_BASE}/${subjectType}/${encodeURIComponent(subjectId)}/section/${encodeURIComponent(section)}`, { params });
    return response.data;
  },

  /**
   * Download the record bundle. Returns a `.zip` (or a bare `.xlsx` when the
   * scope is one section with nothing attached), filename in
   * Content-Disposition. Requires `record.view` AND `record.export`.
   *
   * Rate-limited to 5 per minute per user (429 RATE_LIMIT_EXCEEDED); a bundle
   * over 200 MB is refused (413 RECORD_EXPORT_TOO_LARGE).
   *
   * @returns {Promise<{blob: Blob, filename: string, contentType: string}>}
   */
  exportBundle: async ({ subjectType, subjectId, params, tenantId } = {}) => {
    const query = { ...params };
    if (tenantId) query.tenantId = tenantId;
    const response = await apiService.getSecure(`${RECORD_BASE}/${subjectType}/${encodeURIComponent(subjectId)}/export`, { params: query, responseType: "blob" });

    const disposition = response?.headers?.["content-disposition"] || "";
    const filename = parseContentDispositionFilename(disposition) || `${subjectType}-record-export.zip`;

    const { blob, contentType } = {
      blob: response.data,
      contentType: response?.headers?.["content-type"] || "",
    };
    if (response.data && !(response.data instanceof Blob)) {
      // A JSON error envelope should not be handed to the caller as a blob.
      throw new Error("EXPORT_BLOB_EXPECTED");
    }
    return { blob, filename, contentType };
  },
};

export default RecordService;
